import { AttachMoney as AttachMoneyIcon } from '@mui/icons-material';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import React, { useState, useEffect } from 'react';

import { CommissionAttributionSectionProps, getEffectivePriceCents } from './types';
import { useCatalogInstance } from '../../../hooks/useCatalogInstance';

const CommissionAttributionSection: React.FC<CommissionAttributionSectionProps> = ({
  cart
}) => {
  const { getCatalogDisplayName } = useCatalogInstance();
  const [catalogNames, setCatalogNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadCatalogNames = async () => {
      if (!cart.ready_items) return;

      const names: Record<string, string> = {};
      const uniqueCatalogIds = [...new Set(cart.ready_items.map(item => item.added_catalog_instance_id).filter(Boolean))];

      for (const catalogId of uniqueCatalogIds) {
        if (catalogId) {
          names[catalogId] = await getCatalogDisplayName(catalogId);
        }
      }

      setCatalogNames(names);
    };

    loadCatalogNames();
  }, [cart.ready_items, getCatalogDisplayName]);

  if (!cart.ready_items || cart.ready_items.length === 0) {
    return null;
  }
  return (
    <Card sx={{ mt: 3, border: `1px solid var(--catalog-primary-color, #1976d2)` }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
          <AttachMoneyIcon sx={{ fontSize: 20, color: 'var(--catalog-primary-color, #1976d2)' }} />
          Partner Commission Attribution
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Earnings attribution for catalog partners when services are purchased
        </Typography>

        {/* Individual Service Commissions */}
        <Box sx={{ mb: 3 }}>
          {cart.ready_items.map((item) => (
            <Box key={item.id} sx={{ mb: 2, p: 2, bgcolor: 'success.50', borderRadius: 1, border: 1, borderColor: 'success.200' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {item.service_name || `Service ${item.service_id}`}
                </Typography>
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  ${(getEffectivePriceCents(item) * 0.08 / 100).toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  8% commission rate
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ${(getEffectivePriceCents(item) / 100).toFixed(2)} configured price
                </Typography>
              </Box>
              {item.added_catalog_instance_id && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={`Paid to: ${catalogNames[item.added_catalog_instance_id] || 'Loading...'}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.7rem',
                      height: '20px',
                      borderColor: 'var(--catalog-primary-color, #1976d2)',
                      color: 'var(--catalog-primary-color, #1976d2)'
                    }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* Total Commission Summary */}
        <Box sx={{ p: 3, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Total Partner Earnings
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              ${cart.ready_items.reduce((total, item) => total + (getEffectivePriceCents(item) * 0.08 / 100), 0).toFixed(2)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Estimated commission payments to catalog partners for this order
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CommissionAttributionSection;